/**
 * COMPTABILITÉ — LE TOTAL DE LA NUIT, FAÇON CLASSEUR DE CAISSE.
 *
 * L'exploitant voulait « un vrai total CA avec écart total », et a joint le
 * classeur que l'exploitante remplit chaque nuit. Ce composant en est la
 * version Gestion, lisible d'un coup d'œil :
 *
 *   · le TABLEAU — une ligne par point de vente (Accueil, vestiaire, chaque
 *     bar), le compté par moyen (carte, espèces), le compté, le rapport (le
 *     « Z = » du classeur) et l'écart ; puis les sous-totaux du classeur :
 *     Bars, Service (bars + vestiaire), Accueil ; puis le TOTAL ;
 *   · le VERDICT — total compté, total rapport, ÉCART TOTAL, en grand, et sur
 *     combien de points de vente il porte ;
 *   · le CHIFFRE D'AFFAIRES de la nuit — sur place + préventes en ligne ;
 *   · le RÉCAP ESPÈCES — coupure par coupure, tous postes confondus, et le
 *     chemin vers la remise en banque.
 *
 * Toutes les règles (hors offerts, écart au point de vente, « — » jamais 0)
 * vivent dans le modèle pur `night-sheet-model.ts`, testé sur les chiffres du
 * classeur du 02/10. Ici, on ne fait que peindre.
 *
 * ═══ LA LARGEUR SERT ═══
 * Le poste de l'exploitant fait 2 560 px : quand la carte a la place, le
 * tableau et le verdict se mettent côte à côte (requête de CONTENEUR — la
 * même feuille vit dans la fiche de nuit et dans la colonne de l'assistant,
 * qui n'ont pas la même largeur) ; sinon ils s'empilent.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Banknote, Calculator, Landmark } from 'lucide-react'
import { Link } from '../router.js'
import { formatEcart, formatMoney, tonSeuil } from './money.js'
import { EcartSeuil, SEUILS_DEFAUT, enCentimes, lireJson } from './ClosingCommon.js'
import { commeNuit, phraseSousLigneHorsNuit } from './nights-common.js'
import { nombre } from './entrees.js'
import {
  detailLigne, porteeEcart, REPRISE_SANS_DETAIL,
  type Agregat, type FeuilleCaisse, type LigneFeuille, type NuitAvecPreventes,
} from './night-sheet-model.js'
import './finance-feuille.css'

export type Seuils = { warn: number; alert: number }

/* ═══════════════════════ lectures ═══════════════════════ */

/**
 * Les seuils d'écart des réglages — les mêmes que l'étape « Écarts ». Tant
 * qu'ils ne sont pas lus (ou sans le droit de lire les réglages), ceux par
 * défaut du domaine : ils ne colorent qu'un écart, ils ne le calculent pas.
 */
export function useSeuilsFinance(): Seuils {
  const [seuils, setSeuils] = useState<Seuils>(SEUILS_DEFAUT)
  useEffect(() => {
    let vivant = true
    void lireJson<Seuils>('/api/v1/finance/settings', (v) => {
      const o = typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null
      const s = o && typeof o['settings'] === 'object' && o['settings'] !== null ? (o['settings'] as Record<string, unknown>) : o
      if (!s) return null
      const n = (k: string, d: number): number => (typeof s[k] === 'number' ? (s[k] as number) : d)
      return { warn: n('discrepancy_warn_cents', SEUILS_DEFAUT.warn), alert: n('discrepancy_alert_cents', SEUILS_DEFAUT.alert) }
    }).then((r) => { if (vivant && r.etat === 'ok') setSeuils(r.valeur) })
    return () => { vivant = false }
  }, [])
  return seuils
}

/**
 * La fiche de la nuit, pour ses préventes — relue quand `cle` change (la
 * version de la clôture : une écriture peut avoir rafraîchi le comptage de
 * l'Accueil). `null` tant qu'elle n'est pas lue : les préventes se disent
 * alors « non servies », jamais 0.
 */
export function useNuitFinance(date: string | null, cle?: unknown): NuitAvecPreventes | null {
  const [nuit, setNuit] = useState<NuitAvecPreventes | null>(null)
  useEffect(() => {
    if (!date) { setNuit(null); return }
    let vivant = true
    void lireJson(`/api/v1/finance/nights/${date}`, commeNuit).then((r) => {
      if (vivant && r.etat === 'ok') setNuit(r.valeur as NuitAvecPreventes)
    })
    return () => { vivant = false }
  }, [date, cle])
  return nuit
}

/* ═══════════════════════ peindre ═══════════════════════ */

/** L'ampleur d'un écart contre les seuils : sous la vigilance, au-delà, au-delà de l'alerte. */
function ampleur(cents: number | null, s: Seuils): 'calme' | 'tiede' | 'fort' {
  if (cents === null) return 'calme'
  const a = Math.abs(cents)
  return a >= s.alert ? 'fort' : a >= s.warn ? 'tiede' : 'calme'
}

/** Le lieu sous le nom, seulement s'il apprend quelque chose : « Bar ROOM 1 / ROOM 1 » le répétait. */
function lieuUtile(l: LigneFeuille): boolean {
  if (!l.lieu) return false
  const n = (t: string): string => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()
  return !n(l.nom).includes(n(l.lieu))
}

const PEUT_OUVRIR = new Set(['compte', 'partiel', 'a-compter', 'sans-reponse'])

function Nom(props: { l: LigneFeuille; onPointDeVente?: ((id: string) => void) | undefined }) {
  const { l } = props
  const detail = detailLigne(l)
  const tonDetail = l.etat === 'partiel' || l.etat === 'a-compter' || l.etat === 'sans-reponse' || l.aRattacher
    || l.especesAReporter || l.rapportAttendu
    ? ' ffc-detail--attente' : ''
  const ouvrir = props.onPointDeVente && l.closingPosId && PEUT_OUVRIR.has(l.etat) ? props.onPointDeVente : null
  return (
    <th scope="row">
      {ouvrir ? (
        <button type="button" className="ffc-nom ffc-nom--lien" onClick={() => ouvrir(l.etat === 'sans-reponse' ? 'nuit' : l.closingPosId!)}>
          {l.nom}
        </button>
      ) : <span className="ffc-nom">{l.nom}</span>}
      {lieuUtile(l) ? <span className="lc-hint">{l.lieu}</span> : null}
      {detail ? <span className={`ffc-detail${tonDetail}`}>{detail}</span> : null}
      {/* Les ventes du guichet d'une autre nuit : l'écart de l'Accueil dit
          d'où il vient, sur la ligne même (08/10/2026). */}
      {l.horsNuit.map((h) => (
        <span key={h.cle} className={`ffc-detail ffc-hors-nuit${h.statut === 'EN_ATTENTE' ? ' ffc-detail--attente' : ''}`}>
          {phraseSousLigneHorsNuit(h)}
        </span>
      ))}
    </th>
  )
}

/** Un montant de cellule : « — » quand il est inconnu. */
function M(props: { c: number | null; fort?: boolean; faible?: boolean }) {
  return (
    <span className={`fi-money${props.fort ? ' fi-money--fort' : ''}${props.faible ? ' fi-money--faible' : ''}`}>
      {formatMoney(props.c)}
    </span>
  )
}

function LigneSousTotal(props: { libelle: string; aide: string; a: Agregat; seuils: Seuils; colonnesMoyens: number; classe?: string }) {
  const { a } = props
  return (
    <tr className={`ffc-sous${props.classe ? ` ${props.classe}` : ''}`}>
      <th scope="row">
        <span className="ffc-nom">{props.libelle}</span>
        <span className="lc-hint">{props.aide}</span>
      </th>
      {Array.from({ length: props.colonnesMoyens }, (_, i) => <td key={i} />)}
      <td className="fi-money"><M c={a.compte} fort /></td>
      <td className="fi-money"><M c={a.rapport} faible /></td>
      <td className="fi-money"><EcartSeuil cents={a.ecart} seuils={props.seuils} /></td>
    </tr>
  )
}

/* ═══════════════════════ la feuille ═══════════════════════ */

export function FeuilleDeCaisse(props: {
  feuille: FeuilleCaisse
  seuils: Seuils
  /** Ouvrir l'étape d'un point de vente (l'assistant) ; `'nuit'` pour le périmètre. */
  onPointDeVente?: (cle: string) => void
  /** Les offerts se lisent avec leur droit (`finance.offers.read`) sur la fiche de nuit. */
  offertsVisibles?: boolean
  /** Le chemin vers la remise en banque (`finance.bank.read`). */
  lienBanque?: boolean
  /** Sous le tableau : l'invitation à commencer la clôture, quand il n'y en a pas. */
  invitation?: ReactNode
}) {
  const { feuille: f, seuils } = props
  const avecAutres = f.lignes.some((l) => l.autres !== null)
  const colonnesMoyens = avecAutres ? 3 : 2
  const ouvertes = f.lignes.filter((l) => l.etat !== 'ferme' && l.etat !== 'sans-reponse')
  const aDesBars = f.bars.total > 0
  const aUnVestiaire = f.vestiaire.total > 0
  const aUnAccueil = f.accueil.total > 0
  const autres = ouvertes.filter((l) => l.genre === 'AUTRE')
  const e = formatEcart(f.total.ecart)
  const force = ampleur(f.total.ecart, seuils)
  const portee = porteeEcart(f.total)

  if (f.lignes.length === 0) {
    return (
      <section className="lc-panel ffc">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><Calculator size={17} /></span>
          <h2 className="lc-panel__title">Le total de la nuit</h2>
        </div>
        <div className="lc-panel__body">
          <p className="fi-empty">Aucun point de vente, aucun rapport : rien à totaliser pour cette nuit.</p>
          {props.invitation}
        </div>
      </section>
    )
  }

  return (
    <section className="lc-panel ffc">
      <div className="lc-panel__head ffc-tete">
        <span className="fih-head__icon" aria-hidden="true"><Calculator size={17} /></span>
        <h2 className="lc-panel__title">Le total de la nuit</h2>
        <span className="lc-panel__spacer" />
        <span className="lc-hint">
          {f.avecCloture
            ? 'Ce que le terrain a compté, contre ce que les rapports annoncent — hors offerts'
            : 'Les rapports seuls : la colonne « compté » se remplira avec la clôture'}
        </span>
      </div>

      <div className="lc-panel__body ffc-corps">
        <div className="ffc-grille">
          {/* ═══════════ le tableau du classeur ═══════════ */}
          <div className="ffc-gauche">
            <div className="fi-tablewrap ffc-tablewrap">
              <table className="fi-table fi-table--chiffres ffc-table">
                <colgroup>
                  <col className="ffc-col-nom" />
                  {Array.from({ length: colonnesMoyens + 3 }, (_, i) => <col key={i} className="fi-col-montant" />)}
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Point de vente</th>
                    <th scope="col" className="fi-money">Carte</th>
                    <th scope="col" className="fi-money">Espèces</th>
                    {avecAutres ? <th scope="col" className="fi-money">Autres</th> : null}
                    <th scope="col" className="fi-money">Compté</th>
                    <th scope="col" className="fi-money">Rapport</th>
                    <th scope="col" className="fi-money">Écart</th>
                  </tr>
                </thead>
                <tbody>
                  {f.lignes.map((l) => {
                    const hors = l.etat === 'ferme' || l.etat === 'sans-reponse'
                    return (
                      <tr key={l.cle} className={`ffc-ligne ffc-ligne--${l.etat}${hors ? ' ffc-ligne--hors' : ''}`}>
                        <Nom l={l} onPointDeVente={props.onPointDeVente} />
                        <td className="fi-money"><M c={l.carte} /></td>
                        <td className="fi-money"><M c={l.especes} /></td>
                        {avecAutres ? <td className="fi-money"><M c={l.autres} /></td> : null}
                        <td className="fi-money"><M c={l.compte} fort /></td>
                        <td className="fi-money"><M c={l.rapport} faible /></td>
                        <td className="fi-money">
                          {l.ecart === null ? <span className="fi-money fi-money--faible">—</span> : <EcartSeuil cents={l.ecart} seuils={seuils} />}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tbody className="ffc-sous-totaux">
                  {aDesBars ? (
                    <LigneSousTotal libelle="Bars" aide={`${f.bars.total} point${f.bars.total > 1 ? 's' : ''} de vente`}
                      a={f.bars} seuils={seuils} colonnesMoyens={colonnesMoyens} />
                  ) : null}
                  {aDesBars && aUnVestiaire ? (
                    <LigneSousTotal libelle="Service" aide="Bars et vestiaire"
                      a={f.service} seuils={seuils} colonnesMoyens={colonnesMoyens} />
                  ) : null}
                  {aUnAccueil ? (
                    <LigneSousTotal libelle="Accueil" aide="Le guichet Shotgun"
                      a={f.accueil} seuils={seuils} colonnesMoyens={colonnesMoyens} />
                  ) : null}
                  {autres.length > 0 ? (
                    <LigneSousTotal libelle="Autres points de vente" aide={autres.map((x) => x.nom).join(', ')}
                      a={f.autres} seuils={seuils} colonnesMoyens={colonnesMoyens} />
                  ) : null}
                </tbody>
                <tfoot>
                  <tr className="ffc-total">
                    <th scope="row">
                      <span className="ffc-nom">Total de la nuit sur place</span>
                      <span className="lc-hint">{portee ?? 'Hors offerts'}</span>
                    </th>
                    <td className="fi-money"><M c={f.moyens.carte} /></td>
                    <td className="fi-money"><M c={f.moyens.especes} /></td>
                    {avecAutres ? <td className="fi-money"><M c={f.moyens.autres} /></td> : null}
                    <td className="fi-money"><M c={f.total.compte} fort /></td>
                    <td className="fi-money"><M c={f.total.rapport} /></td>
                    <td className="fi-money"><EcartSeuil cents={f.total.ecart} seuils={seuils} /></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Ce qui manque au total se DIT sous le tableau : un « — » sans
                phrase ressemble à une panne (revue du 07/10/2026). */}
            {f.aReporter > 0 ? (
              <p className="fi-provisoire ffc-note">
                {f.aReporter > 1 ? `${f.aReporter} points de vente ont` : 'Un point de vente a'} des espèces comptées
                qu’aucune ligne Espèces ne porte encore : la finalisation ne les retiendrait pas. Reportez le comptage
                à l’étape du point de vente.
              </p>
            ) : null}
            {f.reprise && f.total.rapport === null ? (
              // Une nuit reprise n'attend rien : dire « pas encore arrivé »
              // promettrait un rapport qui ne viendra jamais (16/10/2026).
              <p className="lc-hint ffc-note">{REPRISE_SANS_DETAIL}.</p>
            ) : f.zapAttendu ? (
              <p className="fi-provisoire ffc-note">
                Le rapport ZAP n’est pas encore arrivé : les bars n’ont pas de rapport, et le total d’après les
                rapports les attend.
              </p>
            ) : f.rapportsAttendus > 0 && !f.accueilMasque ? (
              <p className="fi-provisoire ffc-note">
                {f.rapportsAttendus > 1 ? `${f.rapportsAttendus} points de vente attendent` : 'Un point de vente attend'} leur
                rapport : le total d’après les rapports se lira à son arrivée.
              </p>
            ) : null}
            {f.accueilMasque ? (
              <p className="fi-provisoire ffc-note">
                Le guichet de l’Accueil demande le droit de lire la billetterie : sans lui, le total d’après les
                rapports ne se calcule pas.
              </p>
            ) : null}
            {f.sansReponse > 0 ? (
              <p className="fi-provisoire ffc-note">
                {f.sansReponse} point{f.sansReponse > 1 ? 's' : ''} de vente sans réponse (ouvert ou fermé) :
                {f.sansReponse > 1 ? ' leurs montants sont lus mais restent' : ' son montant est lu mais reste'} hors
                du total. Sans réponse ne veut pas dire fermé.
              </p>
            ) : null}
            {props.offertsVisibles && f.offerts !== null ? (
              <p className="lc-hint ffc-note">
                Hors offerts : {formatMoney(f.offerts)} offerts (carte serveur) ne sont ni une recette, ni comptés,
                ni dans l’écart.
              </p>
            ) : null}
            {props.invitation}
          </div>

          {/* ═══════════ le verdict et le chiffre d'affaires ═══════════ */}
          <div className="ffc-droite">
            <div className={`ffc-verdict ffc-verdict--${force}`}>
              <dl className="ffc-verdict__totaux">
                <div>
                  <dt>Total compté</dt>
                  <dd className="fi-money">{formatMoney(f.total.compte)}</dd>
                </div>
                <div>
                  <dt>Total rapport</dt>
                  <dd className="fi-money">{formatMoney(f.total.rapport)}</dd>
                </div>
              </dl>
              <div className="ffc-verdict__ecart">
                <span className="ffc-verdict__libelle">Écart total</span>
                <span className={`ffc-verdict__valeur fi-seuil--${tonSeuil(f.total.ecart, seuils)}`}>{e.texte}</span>
              </div>
              <p className="ffc-verdict__portee">
                {!f.avecCloture
                  ? 'Se calcule au comptage, point de vente par point de vente.'
                  : f.total.ecart === null
                    ? 'Se calcule dès qu’un point de vente est compté en entier.'
                    : `${portee ?? ''}. ${force === 'fort'
                      ? `Au-delà du seuil d’alerte (${formatMoney(seuils.alert)}) : à expliquer.`
                      : force === 'tiede'
                        ? `Au-delà du seuil de vigilance (${formatMoney(seuils.warn)}).`
                        : `Sous le seuil de vigilance (${formatMoney(seuils.warn)}).`}`}
              </p>
            </div>

            <ChiffreAffaires f={f} />
          </div>

          {/* Le récap des espèces se range SOUS le tableau quand la carte est
              large (la colonne de droite reste courte), après le verdict sinon. */}
          <RecapEspeces f={f} lienBanque={props.lienBanque === true} />
        </div>
      </div>
    </section>
  )
}

/* ═══════════════════════ le chiffre d'affaires de la nuit ═══════════════════════ */

function sourcePreventes(f: FeuilleCaisse): string {
  const p = f.preventes
  if (!p.servies) return 'Pas encore servies par le serveur pour cette nuit.'
  if (p.source === 'SANS_BILLET') return 'Aucun billet vendu en ligne pour cette nuit.'
  const billets = p.billets === null ? '' : ` · ${p.billets} billet${p.billets > 1 ? 's' : ''} en ligne`
  if (p.source === 'REDDITION') return `D’après la reddition ${p.refs.join(', ')}${billets}`.trim()
  if (p.source === 'BILLETS') return `D’après les billets (prix + 1 € de frais rendu par Shotgun, jamais plus que les frais payés), faute de reddition${billets}`
  if (p.source === 'REDDITION_ET_BILLETS') return `D’après la reddition ${p.refs.join(', ')} et, pour une soirée sans reddition, les billets${billets}`
  return 'Rien d’exploitable pour l’instant.'
}

/**
 * Ce que les acheteurs ont payé et ce que Shotgun en garde : le chiffre
 * d'affaires ne compte que ce qu'il reverse (décision du 11/10/2026, la
 * méthode de la DAF). Dit seulement quand les deux sont connus.
 */
function fraisGardes(f: FeuilleCaisse): string | null {
  const { ttc, reversees } = f.preventes
  if (ttc === null || reversees === null || ttc === reversees) return null
  // Dans une phrase, un montant ne se coupe pas avant « € » (recette du 11/10/2026 : « 102,66 / € »).
  const m = (c: number): string => formatMoney(c).replace(/ €$/, '\u00a0€')
  return `Payé par les acheteurs : ${m(ttc)}, dont ${m(ttc - reversees)} de frais gardés par Shotgun, hors chiffre d’affaires`
}

function ChiffreAffaires(props: { f: FeuilleCaisse }) {
  const { f } = props
  const p = f.preventes
  const frais = fraisGardes(f)
  const resteACompter = f.total.total - f.total.comptes
  return (
    <div className="ffc-bloc">
      <h3 className="ffc-bloc__titre">Chiffre d’affaires de la nuit</h3>
      <dl className="ffc-ca">
        <div>
          <dt>Sur place, d’après les rapports</dt>
          <dd className="fi-money">{formatMoney(f.total.rapport)}</dd>
        </div>
        <div>
          <dt>
            Préventes en ligne · reversées par Shotgun
            {p.provisoire ? <span className="ffc-ca__provisoire">provisoire</span> : null}
            <span className="ffc-ca__aide">{sourcePreventes(f)}</span>
            {frais !== null ? <span className="ffc-ca__aide">{frais}</span> : null}
          </dt>
          <dd className="fi-money">{formatMoney(p.reversees)}</dd>
        </div>
        {/* La billetterie des associations (Pretix), 16/10/2026 : une ligne à
            part, seulement quand elle entre au total (réglage allumé, ou
            arrêtée par la finalisation). Éteinte, la fiche la montre ailleurs
            comme un repère, jamais ici. */}
        {f.associations.dansLeChiffre ? (
          <div>
            <dt>
              Billetterie des associations · Pretix
              <span className="ffc-ca__aide">
                {f.figee
                  ? 'Valeur faciale moins la part de chaque association, figée à la finalisation'
                  : `Valeur faciale moins la part de chaque association · ${f.associations.billets} billet${f.associations.billets > 1 ? 's' : ''} vendu${f.associations.billets > 1 ? 's' : ''}`}
              </span>
            </dt>
            <dd className="fi-money">{f.associations.cachee ? 'réservé' : formatMoney(f.figee ? f.associations.arretee : f.associations.direct)}</dd>
          </div>
        ) : null}
        <div className="ffc-ca__total">
          <dt>
            D’après les rapports
            {f.ca.rapport === null && f.reprise ? (
              <span className="ffc-ca__aide">{REPRISE_SANS_DETAIL}</span>
            ) : f.ca.rapport === null && (f.zapAttendu || f.rapportsAttendus > 0) ? (
              <span className="ffc-ca__aide">Le rapport ZAP n’est pas encore arrivé : le total attend les bars</span>
            ) : null}
          </dt>
          <dd className="fi-money">{formatMoney(f.ca.rapport)}</dd>
        </div>
        <div className="ffc-ca__total">
          <dt>
            D’après le comptage
            {f.ca.compte === null && f.avecCloture && resteACompter > 0 ? (
              <span className="ffc-ca__aide">
                Quand tout sera compté : encore {resteACompter} point{resteACompter > 1 ? 's' : ''} de vente
              </span>
            ) : null}
          </dt>
          <dd className="fi-money">{formatMoney(f.ca.compte)}</dd>
        </div>
        {/* Finalisée, la nuit a un ARRÊTÉ : le retenu prime sur le constaté.
            C'est lui qui est versé au mois, pas le compté. */}
        {f.figee ? (
          <div className="ffc-ca__total">
            <dt>
              Arrêté à la finalisation
              <span className="ffc-ca__aide">
                {f.preventesArretees !== null
                  ? `Le retenu de chaque ligne, hors offerts, + les préventes reversées figées à la finalisation (${formatMoney(f.preventesArretees)})`
                  : 'Le retenu de chaque ligne, hors offerts, + préventes reversées'}
                {f.associations.arretee ? ` + la billetterie des associations Pretix, parts déduites (${formatMoney(f.associations.arretee)})` : ''}
              </span>
              {f.preventesArretees !== null && p.reversees !== null && p.reversees !== f.preventesArretees ? (
                <span className="ffc-ca__aide">
                  Les préventes reversées valent aujourd’hui {formatMoney(p.reversees)} : le chiffre arrêté ne bouge pas, une révision de la clôture le mettrait à jour
                </span>
              ) : null}
            </dt>
            <dd className="fi-money">{formatMoney(f.ca.arrete)}</dd>
          </div>
        ) : null}
      </dl>
      {p.notes.length > 0 ? (
        <details className="ffc-plis">
          <summary>D’où viennent les préventes</summary>
          <ul>{p.notes.map((n) => <li key={n}>{n}</li>)}</ul>
        </details>
      ) : null}
    </div>
  )
}

/* ═══════════════════════ le récap des espèces ═══════════════════════ */

function RecapEspeces(props: { f: FeuilleCaisse; lienBanque: boolean }) {
  const { especes: s, moyens } = props.f
  const tableau = enCentimes(moyens.especes)
  const differe = s.total !== null && tableau !== null && s.total !== tableau
  return (
    <div className="ffc-bloc ffc-especes">
      <div className="ffc-especes__tete">
        <h3 className="ffc-bloc__titre"><Banknote size={16} aria-hidden="true" />Récap espèces</h3>
        {s.total !== null ? (
          <p className="ffc-especes__total">
            <span>Total espèces comptées</span>
            <span className="fi-money">{formatMoney(s.total)}</span>
          </p>
        ) : null}
      </div>
      {!props.f.avecCloture ? (
        <p className="lc-hint">Les espèces se comptent par coupure à la clôture, point de vente par point de vente.</p>
      ) : s.total === null ? (
        <p className="lc-hint">Aucune espèce comptée pour l’instant.</p>
      ) : (
        <>
          {s.coupures.length > 0 ? (
            <ul className="ffc-coupures" aria-label="Espèces par coupure, tous points de vente">
              {s.coupures.map((c) => (
                <li key={c.valeur} className={c.valeur >= 500 ? 'ffc-coupure' : 'ffc-coupure ffc-coupure--piece'}>
                  <span className="ffc-coupure__valeur">{formatMoney(c.valeur)}</span>
                  <span className="ffc-coupure__nombre">× {nombre(c.quantite)}</span>
                  <span className="ffc-coupure__montant fi-money">{formatMoney(c.montant)}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {s.saisiesDirectes.length > 0 ? (
            <ul className="ffc-directes">
              {s.saisiesDirectes.map((x) => (
                <li key={x.nom}>
                  <span>{x.nom} · total saisi sans coupures</span>
                  <span className="fi-money">{formatMoney(x.montant)}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {differe ? (
            <p className="fi-reserve">
              Le tableau retient {formatMoney(tableau)} d’espèces : un point de vente a saisi ses espèces
              autrement qu’en coupures, ou n’a pas reporté son comptage. La remise se prépare sur les coupures.
            </p>
          ) : null}
        </>
      )}
      {props.lienBanque ? (
        <Link className="lc-btn lc-btn--sm ffc-banque" href="/comptabilite/banque">
          <Landmark size={15} aria-hidden="true" />Préparer la remise en banque
        </Link>
      ) : null}
    </div>
  )
}
